import _ from 'underscore';
import $ from 'jquery';
import React, { useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useReportDataSource } from '@bingads-webui-campaign/reports-v4-data-source';
import { Callout, mergeStyleSets } from '@fluentui/react';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { DateRangeEditor, DurableDropdown } from '@bingads-webui-campaign/inline-download-date-range-editor';
import { DateRangeFormat } from '@bingads-webui-campaign/date-range-utilities';
import { TimeRangeSelector, Warning, getToday, getDataObject, getApiObject } from '@bingads-webui-campaign/report-create';
import { schema as reportSchemas } from '@bingads-webui-campaign/reports-v4-schema';
import { get } from '@bingads-webui-universal/primitive-utilities';
import { isReportTemplateType } from '../shared';

const styles = mergeStyleSets({
  callout: {
    padding: '24px 16px',
    background: '#fff',
    '& .ms-Callout-main': {
      position: 'initial',
      overflow: 'visible',
    },
  },
  actions: {
    marginTop: 16,
  },
  spinner: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: '3',
    background: 'rgba(255, 255, 255, .6)',
  },
  editor: {
    '& ul': {
      padding: 0,
      '& li': {
        listStyle: 'none',
        '& a[role=button]': {
          padding: '6px 10px',
          color: '#000',
          display: 'flex',
          alignItems: 'center',
          textDecoration: 'none',
          ':hover': {
            backgroundColor: '#f3f2f1',
          },
          '& .glyphicon-ok': {
            display: 'none',
          },
          '& .kendo-date-picker': {
            minWidth: 100,
            marginLeft: 5,
            marginRight: 5,
          },
        },
        '& a[role=button].selected': {
          backgroundColor: '#0078d4',
          color: '#fff',
        },
      },
    },
  },
  cancelBtn: {
    marginLeft: 8,
  },
});

function InlineEditDateRange(props) {
  const {
    target, item, legacyI18n, onDismiss, onActionSuccess, notifyErrorPipe,
  } = props;
  const [loading, setLoading] = useState(false);
  const i18n = useContext(I18nContext);
  const reportApi = useReportDataSource();
  const isReportTemplate = isReportTemplateType(item.Type);

  // report template
  const reportTemplateModel = getDataObject(item, { preview: true }).general;
  const [reportTemplateDataRange, setReportTemplateDataRange] = useState(reportTemplateModel.timeRange);
  const [reportTemplateWarnings, setReportTemplateWarnings] = useState({});

  // email rule
  const emailRuleModel = get(item, 'Selection.DateRange');
  const dateRangeFormat = new DateRangeFormat({ i18n: legacyI18n });
  const [emailDateRange, setEmailDateRange] = useState(emailRuleModel);
  const dropdownRef = useRef({});
  const updateEmailRuleDateRange = (data) => {
    setEmailDateRange(data);
    if (data.DateRangePreset) { // A date range other than Custom is selected
      dropdownRef.current.setState({ expanded: false });
    }
  };

  async function saveDateRange() {
    setLoading(true);
    try {
      const originTaskItem = await reportApi.getTaskItem(item.Id);
      let newTaskItem = {};
      if (isReportTemplate) {
        const updates = _.pick(getApiObject({
          general: {
            timeRange: reportTemplateDataRange,
          },
        }, { preview: true }), 'ReportTime');
        newTaskItem = { ...originTaskItem, ...updates };
      } else {
        const { ...taskItemToSave } = originTaskItem;
        taskItemToSave.Selection.DateRange = emailDateRange;
        newTaskItem = taskItemToSave;
      }
      await reportApi.patchTaskItem(newTaskItem, { isEmailRule: !isReportTemplate });
      onDismiss();
      onActionSuccess();
    } catch (e) {
      notifyErrorPipe(e);
    } finally {
      setLoading(false);
    }
  }

  function handleDismiss(e) {
    const calendarContainer = $('.k-animation-container .k-calendar-container');
    if (calendarContainer.find(e.target).length > 0) {
      return;
    }
    props.onDismiss();
  }

  function renderReportTemplate() {
    const reportType = item.ReportType;
    const reportSchema = reportSchemas[reportType];

    return (
      <React.Fragment>
        <TimeRangeSelector
          validationProps={{ field: 'timeRange', alwaysShowError: false }}
          value={reportTemplateDataRange}
          onChange={val => setReportTemplateDataRange(val)}
          i18n={i18n}
          reportSchema={reportSchema}
          setWarning={val => setReportTemplateWarnings({ ...reportTemplateWarnings, timeRange: val })}
          timeGrain={reportTemplateModel.timeGrain}
          timeZone={reportTemplateModel.timeZone}
          today={getToday(i18n, reportTemplateModel.timeZone)}
        />
        <Warning messages={reportTemplateWarnings} />
      </React.Fragment>
    );
  }

  function renderEmailRule() {
    return (
      <DurableDropdown
        containerClass={styles.editor}
        button={dateRangeFormat.format(emailDateRange)}
        ref={dropdownRef}
      >
        <DateRangeEditor
          i18n={legacyI18n}
          data={emailDateRange}
          onChange={updateEmailRuleDateRange}
        />
      </DurableDropdown>
    );
  }

  return (
    <Callout
      coverTarget
      setInitialFocus
      doNotLayer
      preventDismissOnScroll
      directionalHint={0}
      isBeakVisible={false}
      target={document.querySelector(target).getBoundingClientRect()}
      className={`${styles.callout} inline-edit-popup`}
      onDismiss={handleDismiss}
    >
      {isReportTemplate ? renderReportTemplate() : renderEmailRule()}
      <div className={styles.actions}>
        <PrimaryButton onClick={saveDateRange} text={i18n.getString(_TL_('Save'))} />
        <DefaultButton className={styles.cancelBtn} onClick={onDismiss} text={i18n.getString(_TL_('Cancel'))} />
      </div>
      {loading && <Spinner className={styles.spinner} label={i18n.getString(_TL_('Loading...'))} labelPosition="right" />}
    </Callout>
  );
}

InlineEditDateRange.propTypes = {
  item: PropTypes.shape({}).isRequired,
  target: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  legacyI18n: PropTypes.shape({}).isRequired,
  onDismiss: PropTypes.func.isRequired,
  onActionSuccess: PropTypes.func.isRequired,
  notifyErrorPipe: PropTypes.func.isRequired,
};

export default InlineEditDateRange;
